import { useState } from 'react';
import { ELSButton, ELSIcon } from 'components/common/els';
import { CHARACTER_LIMIT, CollectionColumn, CollectionConfig, characterCountText } from './constants';

export type InlineRow = Record<string, string>;

export interface AnnualAmbulatoryCareInlineCollectionProps {
  config: CollectionConfig;
  rows: InlineRow[];
  isAuthor: boolean;
  /** Commits the new row list to the chart collection. */
  onReplace: (rows: InlineRow[]) => void;
}

const AnnualAmbulatoryCareInlineCollection = ({ config, rows, isAuthor, onReplace }: AnnualAmbulatoryCareInlineCollectionProps) => {
  const [draft, setDraft] = useState<InlineRow>({});

  const setDraftValue = (field: string, value: string) => setDraft((prev) => ({ ...prev, [field]: value }));

  const hasValue = (column: CollectionColumn): boolean => Boolean(String(draft[column.field] ?? '').trim());
  const canAdd = config.columns.some(hasValue);

  const handleAdd = () => {
    if (!canAdd) return;
    onReplace([...rows, draft]);
    setDraft({});
  };

  const handleDelete = (rowIndex: number) => onReplace(rows.filter((_, index) => index !== rowIndex));

  const formatCell = (row: InlineRow, column: CollectionColumn): string => String(row[column.field] ?? '').trim();

  return (
    <div className="annual-ambulatory-care__inline-collection">
      {rows.length > 0 && (
        <table className="annual-ambulatory-care__table">
          <thead>
            <tr>
              {config.columns.map((column) => (
                <th key={column.field}>{column.header}</th>
              ))}
              {isAuthor && <th aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={rowIndex}>
                {config.columns.map((column) => (
                  <td key={column.field}>{formatCell(row, column)}</td>
                ))}
                {isAuthor && (
                  <td className="annual-ambulatory-care__row-actions">
                    <button className="annual-ambulatory-care__row-delete" type="button" onClick={() => handleDelete(rowIndex)} aria-label="Delete row">
                      <ELSIcon name="trash" prefix="gizmo" color="secondary" size="1x" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {isAuthor && (
        <>
          <div className="annual-ambulatory-care__inline-fields">
            {config.columns.map((column) => (
              <div className="annual-ambulatory-care__inline-field" key={column.field}>
                <span className="c-els-field__label">{column.header}</span>
                <textarea
                  className="c-els-field__input annual-ambulatory-care__inline-textarea"
                  value={draft[column.field] ?? ''}
                  maxLength={CHARACTER_LIMIT}
                  aria-label={column.header}
                  onChange={(event) => setDraftValue(column.field, event.target.value)}
                />
                <div className="annual-ambulatory-care__char-count">{characterCountText(draft[column.field], CHARACTER_LIMIT)}</div>
              </div>
            ))}
          </div>

          <ELSButton
            className="annual-ambulatory-care__add-btn"
            text="Add"
            type="secondary"
            iconName="add-circle"
            iconPrefix="hmds"
            iconPosition="right"
            iconSize="1x"
            isDisabled={!canAdd}
            onClick={handleAdd}
          />
        </>
      )}
    </div>
  );
};

AnnualAmbulatoryCareInlineCollection.displayName = 'AnnualAmbulatoryCareInlineCollection';
export default AnnualAmbulatoryCareInlineCollection;
